import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import {
  TemplateData,
  WhatsappBulkPayload,
  WhatsappSendItem,
  WhatsappSendTemplatePayload,
  ScheduledMessage,
  WhatsappTemplateSearchResponse,
  WhatsappTemplateSearchParams,
  BroadcastBulkPayload,
} from "../../types/TemplateType";


export const fetchWhatsappSendList = createAsyncThunk<
  WhatsappTemplateSearchResponse,
  WhatsappTemplateSearchParams | undefined,
  { rejectValue: string }
>(
  "whatsapp/fetchSendList",
  async (params, thunkAPI) => {
    try {
      const size = params?.size ?? 10;
      const searchParams = new URLSearchParams();

      searchParams.set("page", String(params?.page ?? 0));
      searchParams.set("size", String(size));

      if (params?.status) searchParams.set("status", params.status);
      if (params?.after) searchParams.set("after", params.after);
      const response = await customFetch.get(
        `/api/whatsapp/send/search?${searchParams.toString()}`
      );

      let payload = response.data;
      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return {
        results: payload.data.results,
        page: payload.data.page,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch WhatsApp send list"
      );
    }
  }
);


export const sendWhatsappTemplate = createAsyncThunk<
  any,
  WhatsappSendTemplatePayload,
  { rejectValue: string }
>(
  "whatsapp/sendTemplate",
  async (payload, thunkAPI) => {
    try {
      const response = await customFetch.post("/api/whatsapp/send/template", payload);
      console.log("SEND RESPONSE:", response.data); // ← add this line


      if (response.data.status !== 1) {
        throw new Error(response.data.error || "Failed to send message");
      }

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error ||
        error?.message ||
        "Failed to send WhatsApp message"
      );
    }
  }
);


export const sendWhatsappBulkTemplate = createAsyncThunk<
  any,
  WhatsappBulkPayload,
  { rejectValue: string }
>(
  "whatsapp/sendBulkTemplate",
  async (payload, thunkAPI) => {
    try {
      const formData = new FormData();

      formData.append("templateName", payload.templateName);
      formData.append("language", payload.language);

      if (payload.headerMediaId) formData.append("headerMediaId", payload.headerMediaId);
      if (payload.headerType) formData.append("headerType", payload.headerType);
      (payload.headerParams || []).forEach((val) => formData.append("headerParams", val));
      (payload.bodyParams || []).forEach((val) => formData.append("bodyParams", val));
      (payload.buttonUrlParams || []).forEach((val) => formData.append("buttonUrlParams", val));
      (payload.customerIds || []).forEach((id) => formData.append("customerIds", String(id)));
      (payload.mobileNumbers || []).forEach((num) => formData.append("mobileNumbers", num));
      if (payload.file) formData.append("file", payload.file);

      const response = await customFetch.post("/whatsapp/template/bulk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status !== 1) {
        throw new Error(response.data.error || "Failed to send bulk message");
      }

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error ||
        error?.message ||
        "Failed to send bulk WhatsApp message"
      );
    }
  }
);


export const scheduleWhatsappBulkTemplate = createAsyncThunk<
  any,
  WhatsappBulkPayload & { scheduleDate: string; scheduleTime: string; scheduleType?: "ONCE" | "DAILY" },
  { rejectValue: string }
>(
  "whatsapp/scheduleBulkTemplate",
  async (payload, thunkAPI) => {
    try {
      const response = await customFetch.post(
        "/api/whatsapp/schedule",
        {
          templateName: payload.templateName,
          language: payload.language,
          headerMediaId: payload.headerMediaId ?? null,
          headerParams: payload.headerParams || [],
          bodyParams: payload.bodyParams || [],
          buttonUrlParams: payload.buttonUrlParams || [],
          customerIds: payload.customerIds || [],
          mobileNumbers: payload.mobileNumbers || [],
          scheduleDate: payload.scheduleDate,
          scheduleTime: payload.scheduleTime,
          scheduleType: payload.scheduleType,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.status !== 1) {
        throw new Error(response.data.error || "Failed to schedule message");
      }

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error ||
        error?.message ||
        "Failed to schedule WhatsApp bulk message"
      );
    }
  }
);


export const createWhatsappTemplate = createAsyncThunk<
  any,
  TemplateData,
  { rejectValue: string }
>("whatsapp/createTemplate", async (templateData, thunkAPI) => {
  try {
    const components: any[] = [];

    if (templateData.headerType) {
      if (templateData.headerType === "Text") {
        components.push({
          type: "HEADER",
          format: "TEXT",
          text: templateData.headerText || "",
        });
      } else if (["Image", "Video", "Document"].includes(templateData.headerType)) {
        components.push({
          type: "HEADER",
          format: templateData.headerType.toUpperCase(),
          ...(templateData.mediaHandle && {
            mediaHandle: templateData.mediaHandle,
          }),
        });
      }
    }

    if (templateData.bodyText) {
      components.push({ type: "BODY", text: templateData.bodyText });
    }

    if (templateData.footerText) {
      components.push({ type: "FOOTER", text: templateData.footerText });
    }

    if (templateData.buttons?.length) {
      const buttonConfigs = templateData.buttons.map((button) => {
        const config: any = {
          type:
            button.type === "Quick Reply"
              ? "QUICK_REPLY"
              : button.type === "URL"
                ? "URL"
                : "PHONE_NUMBER",
          text: button.text,
        };

        if (button.type === "URL" && button.url) config.url = button.url;
        if (button.type === "Call to Action" && button.phoneNumber)
          config.phoneNumber = button.phoneNumber;

        return config;
      });

      components.push({ type: "BUTTONS", buttons: buttonConfigs });
    }

    const payload = {
      templateName: templateData.templateName,
      language: templateData.language === "English" ? "en_US" : "en_US",
      category: templateData.category?.toUpperCase(),
      components,
    };

    const response = await customFetch.post("api/v1/templates/create", payload);

    if (!response.data.success) {
      throw new Error(response.data.message || "Template creation failed");
    }

    const parsedResponse = JSON.parse(response.data.response);
    return { ...response.data, response: parsedResponse };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.message || "Template creation failed"
    );
  }
});

export const fetchScheduledMessages = createAsyncThunk<
  ScheduledMessage[],
  void,
  { rejectValue: string }
>(
  "whatsapp/fetchScheduled",
  async (_, thunkAPI) => {
    try {
      const response = await customFetch.get("/api/whatsapp/scheduled");
      if (response.data.status !== 1) {
        throw new Error(response.data.error || "Failed to fetch scheduled messages");
      }
      return response.data.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error || error?.message || "Failed to fetch scheduled messages"
      );
    }
  }
);

export const uploadWhatsappMedia = async (file: File): Promise<{ headerHandle: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await customFetch.post(
    "api/whatsapp/media/upload",
    formData,
    {
      headers: {
        "Content-Type": undefined,
      },
    }
  );

  if (!response.data?.headerHandle) {
    throw new Error(response.data?.error || "Media upload failed");
  }

  return {
    headerHandle: response.data.headerHandle,
  };
};

export const createWhatsappTemplateVariable = createAsyncThunk<
  any,
  { templateData: TemplateData },
  { rejectValue: string }
>("whatsapp/createTemplate", async ({ templateData }, thunkAPI) => {
  try {
    const components: any[] = [];

    if (templateData.headerType) {
      if (templateData.headerType === "Text") {
        const headerComp: any = {
          type: "HEADER",
          format: "TEXT",
          text: templateData.headerText || "",
        };

        const filledHeaderExamples = (templateData.headerExamples || []).filter(Boolean);
        if (filledHeaderExamples.length > 0) {
          headerComp.examples = filledHeaderExamples

        }

        components.push(headerComp);
      } else if (["Image", "Video", "Document"].includes(templateData.headerType)) {
        components.push({
          type: "HEADER",
          format: templateData.headerType.toUpperCase(),
          ...(templateData.mediaHandle && {
            mediaHandle: templateData.mediaHandle,
          }),
        });
      }
    }

    if (templateData.bodyText) {
      const bodyComp: any = { type: "BODY", text: templateData.bodyText };

      const filledBodyExamples = (templateData.bodyExamples || []).filter(Boolean);
      if (filledBodyExamples.length > 0) {
        bodyComp.examples = filledBodyExamples;
      }

      components.push(bodyComp);
    }

    if (templateData.footerText) {
      components.push({ type: "FOOTER", text: templateData.footerText });
    }

    if (templateData.buttons?.length) {
      const buttonConfigs = templateData.buttons.map((button) => {
        const config: any = {
          type:
            button.type === "Quick Reply"
              ? "QUICK_REPLY"
              : button.type === "URL"
                ? "URL"
                : "PHONE_NUMBER",
          text: button.text,
        };

        if (button.type === "URL" && button.url) {
          config.url = button.url;
          if (button.url.includes("{{1}}") && button.urlExample) {
            config.example = [button.urlExample];
          }
        } if (button.type === "Call to Action" && button.phoneNumber)
          config.phoneNumber = button.phoneNumber;

        return config;
      });

      components.push({ type: "BUTTONS", buttons: buttonConfigs });
    }

    const payload = {
      templateName: templateData.templateName,
      language: templateData.language === "English" ? "en_US" : "en_US",
      category: templateData.category?.toUpperCase(),
      components,
    };

    const response = await customFetch.post("api/v1/templates/create", payload);

    if (!response.data.success) {
      throw new Error(response.data.error?.message || response.data.message || "Template creation failed"
      );
    }

    const parsedResponse = JSON.parse(response.data.response);
    return { ...response.data, response: parsedResponse };
  } catch (error: any) {
    const message =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      error?.message ||
      "Template creation failed";

    return thunkAPI.rejectWithValue(message);
  }
});

export const sendWhatsappBroadcastBulk = createAsyncThunk<
  any,
  BroadcastBulkPayload,
  { rejectValue: string }
>(
  "whatsapp/broadcastBulk",
  async (payload, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("templateName", payload.templateName);
      formData.append("language", payload.language);
      if (payload.file) formData.append("file", payload.file);

      (payload.mobileNumbers || []).forEach((num) => formData.append("mobileNumbers", num));
      (payload.headerParams || []).forEach((v) => formData.append("headerParams", v));
      (payload.bodyParams || []).forEach((v) => formData.append("bodyParams", v));
      (payload.buttonUrlParams || []).forEach((v) => formData.append("buttonUrlParams", v));

      // Only when scheduling
      if (payload.scheduledAt) formData.append("scheduledAt", payload.scheduledAt);
      if (payload.timeZone) formData.append("timeZone", payload.timeZone);

      // Only for templates with an IMAGE/VIDEO/DOCUMENT header
      if (payload.headerMediaId) formData.append("headerMediaId", payload.headerMediaId);
      if (payload.headerType) formData.append("headerType", payload.headerType);

      const response = await customFetch.post("/api/broadcasts/bulk", formData, {
        // Let the browser set multipart boundary
        headers: { "Content-Type": undefined },
      });

      const data = response.data;
      if (data?.success === false || data?.status === 0) {
        throw new Error(data?.error || data?.message || "Failed to send broadcast");
      }

      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send broadcast"
      );
    }
  }
);

export const fetchTemplateLibrary = createAsyncThunk<
  WhatsappTemplateSearchResponse,
  WhatsappTemplateSearchParams | undefined,
  { rejectValue: string }
>(
  "whatsapp/fetchTemplateLibrary",
  async (params, thunkAPI) => {
    try {
      const size = params?.size ?? 10;
      const searchParams = new URLSearchParams();

      searchParams.set("page", String(params?.page ?? 0));
      searchParams.set("size", String(size));
      if (params?.after) searchParams.set("after", params.after);

      const response = await customFetch.get(
        `/api/template-library/library/search?${searchParams.toString()}`
      );

      let payload = response.data;
      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return {
        results: payload.data.results,
        page: payload.data.page,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch template library"
      );
    }
  }
);

export const createLibraryTemplate = createAsyncThunk<
  any,
  { templateData: TemplateData; title: string; description: string; industry: string },
  { rejectValue: string }
>("whatsapp/createLibraryTemplate", async ({ templateData, title, description, industry }, thunkAPI) => {
  try {
    const components: any[] = [];

    if (templateData.headerType === "Text") {
      components.push({
        type: "HEADER",
        format: "TEXT",
        text: templateData.headerText || "",
        examples: (templateData.headerExamples || []).filter(Boolean),
      });
    } else if (["Image", "Video", "Document"].includes(templateData.headerType)) {
      components.push({
        type: "HEADER",
        format: templateData.headerType.toUpperCase(),
        mediaHandle: templateData.mediaHandle || null,
      });
    }

    if (templateData.bodyText) {
      components.push({
        type: "BODY",
        text: templateData.bodyText,
        examples: (templateData.bodyExamples || []).filter(Boolean),
      });
    }

    if (templateData.footerText) {
      components.push({ type: "FOOTER", text: templateData.footerText });
    }

    if (templateData.buttons?.length) {
      components.push({
        type: "BUTTONS",
        buttons: templateData.buttons.map((b) => ({
          type: b.type === "Quick Reply" ? "QUICK_REPLY" : b.type === "URL" ? "URL" : "PHONE_NUMBER",
          text: b.text,
          url: b.type === "URL" ? b.url || null : null,
          phoneNumber: b.type === "Call to Action" ? b.phoneNumber || null : null,
        })),
      });
    }

    const response = await customFetch.post("/api/template-library/library", {
      title,
      description,
      industry,
      templateName: templateData.templateName,
      language: "en_US",
      category: templateData.category?.toUpperCase(),
      components,
    });

    if (response.data.status !== 1) {
      throw new Error(response.data.error || "Failed to create example template");
    }

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error || error?.message || "Failed to create example template"
    );
  }
});
